import { z } from "zod";

/**
 * Contrat du module Silhouettes de la console (spec du 21 septembre 2026, issue #145) :
 * deposer une image, regler, voir l'apercu, telecharger le 3MF, sans terminal.
 *
 * La meme requete fait les deux sauts : du navigateur au backend de la console
 * (`POST /api/silhouettes/rendu`), puis du backend au service Python `products`
 * (`POST /silhouette/render`). La reponse revient de meme, telle que `products` la rend.
 * Rien n'est garde sur fortress : les fichiers repartent dans la reponse, en base64.
 *
 * Les noms de champs sont ceux de l'outil (`packages/products/silhouette`), en francais et
 * en serpent : c'est le contrat de donnees de `service.py`, pas du code de la console.
 */

/** Le fichier choisi par Nicolas, avant toute reduction : au-dela, refuse dans le navigateur. */
export const IMAGE_SOURCE_OCTETS_MAX = 10 * 1024 * 1024;

/**
 * L'image une fois reduite dans le navigateur. Le frontal nginx du template refuse un corps
 * de plus de 2 Mo (`client_max_body_size 2m`) : 1 450 000 octets font 1 933 336 caracteres
 * en base64 (remplissage compris), et laissent la place des reglages.
 */
export const IMAGE_ENVOI_OCTETS_MAX = 1_450_000;

/** Le champ `image` en base64, borne cote serveur : un peu plus que l'envoi le plus lourd. */
export const IMAGE_BASE64_MAX = 2_000_000;

/**
 * Le grand cote d'une image detouree envoyee (sa transparence sert, elle part en PNG), en
 * pixels : au-dela, elle est redessinee plus petite. Les images de Nicolas font 960 px, et a
 * 1 200 px une silhouette de 121 mm tient deja 0,1 mm par pixel, bien assez pour imprimer.
 */
export const COTE_ENVOI_PX = 1200;

/**
 * Le grand cote d'une image opaque envoyee (elle part en JPEG, sur fond blanc) : c'est l'outil
 * qui la detoure, et c'est la que le calcul coute. Un dessin de ChatGPT detaille prend 13 a
 * 20 s a 1 024 px, 2,5 a 4,6 s a 800 ; le chevalier, a 1 024 px en couleurs auto, a pris
 * 13,9 puis 16,1 s, puis a ete coupe a 20 s au troisieme passage (#145). Une image de 800 px
 * au plus et assez legere part telle quelle, sans que sa transparence soit lue.
 */
export const COTE_ENVOI_OPAQUE_PX = 800;

/** Le plus petit palier de la reduction : en dessous, le trait ne tiendrait plus. */
export const COTE_ENVOI_MIN_PX = 600;

/**
 * Delai du backend vers `products`, en secondes. Les delais s'emboitent sous les 30 s du
 * frontal nginx (`proxy_read_timeout 30s`) : 15 s d'attente du verrou dans `products`, puis
 * le rendu, et la console repond avant que nginx ne coupe.
 */
export const CALCUL_DELAI_S = 25;

export const HAUTEUR_MIN_MM = 10;
export const HAUTEUR_MAX_MM = 400;
export const COULEURS_MAX = 8;
export const NOM_FICHIER_MAX = 200;

/** La vue de face d'une reponse de rendu (`face_svg`, #337), en caracteres : au-dela, refusee. */
const FACE_SVG_MAX = 600_000;

/**
 * La hauteur de la ligne de sol imposee a la main (`--sol-hauteur`, issue #175), en
 * millimetres. **Large a dessein** : les vraies bornes sont celles de l'outil, qui dependent
 * de la piece (au moins la largeur minimale du trait, au plus un dixieme de la hauteur), et
 * lui seul les connait. La console ne garde ici qu'un garde-fou : elle refuse ce qui n'aurait
 * de sens pour aucune piece, et laisse l'outil refuser le reste par un 400 avec son message.
 */
export const SOL_HAUTEUR_MIN_MM = 1;
export const SOL_HAUTEUR_MAX_MM = 40;

/**
 * La largeur sous laquelle un trait de couleur ne sort pas, en millimetres, quand le rapport ne la
 * donne pas (`PASTILLE_LARGEUR_MIN_MM_DEFAUT` de l'outil, #397) : deux cordons d'une buse de 0,4 mm.
 * C'est l'outil qui la fixe et la publie dans le rapport (`pastille_largeur_min_mm`) ; la page ne
 * la prend ici que tant que `products` n'est pas deploye.
 */
export const PASTILLE_LARGEUR_MIN_MM_DEFAUT = 0.8;

/**
 * Un caractere de controle (C0, DEL et C1), ou un separateur de ligne ou de paragraphe : la
 * meme regle que `products` pour le nom de fichier, qui finit dans un rapport et un message.
 */
export function estUnControle(code: number): boolean {
  return code <= 0x1f || (code >= 0x7f && code <= 0x9f) || code === 0x2028 || code === 0x2029;
}

/** Le nom des fichiers rendus : minuscules, chiffres et tirets, 40 caracteres au plus. */
export const NOM_MOTIF = /^[a-z0-9-]{1,40}$/;

/** Le seul profil d'attache livre avec l'outil. `null` : pas d'attache. */
export const ATTACHE_BASE_MODULAIRE = "base-modulaire";

export const SOLS = ["auto", "oui", "non"] as const;
export const MODES = ["fidele", "epaissir"] as const;

/**
 * Les deux cotes de la vue de face (#337). `cote_mur` dit lequel est tourne vers le mur du
 * serre-livres : **gauche et droite de la vue de face rendue**, avant tout miroir.
 */
export const COTES_MUR = ["gauche", "droite"] as const;
export type CoteMur = (typeof COTES_MUR)[number];

/** Les reglages de la page, bornes comme `service.py` les borne. */
export const reglagesSchema = z.object({
  nom: z.string().regex(NOM_MOTIF),
  hauteur_mm: z.number().min(HAUTEUR_MIN_MM).max(HAUTEUR_MAX_MM),
  attache: z.literal(ATTACHE_BASE_MODULAIRE).nullable(),
  /** `auto`, un nombre impose de 1 a 8, ou `null` : une seule couleur. */
  couleurs: z.union([z.literal("auto"), z.number().int().min(1).max(COULEURS_MAX)]).nullable(),
  sol: z.enum(SOLS),
  /**
   * La hauteur de la ligne de sol, imposee a la main (#175). **Facultative et nullable** :
   * absente ou `null`, c'est l'outil qui la decide, en relevant la ligne juste assez pour
   * attraper un second appui. La page n'envoie la cle que lorsque le champ porte un nombre.
   */
  sol_hauteur_mm: z.number().min(SOL_HAUTEUR_MIN_MM).max(SOL_HAUTEUR_MAX_MM).nullable().optional(),
  mode: z.enum(MODES),
  /**
   * Le cote de la vue de face tourne vers le mur du serre-livres, celui de l'apercu avant tout
   * miroir (#337). **Facultatif et nullable**, comme `sol_hauteur_mm` : absent ou `null`, l'outil
   * juge les deux sens, et rien ne change pour une silhouette seule.
   */
  cote_mur: z.enum(COTES_MUR).nullable().optional(),
  /**
   * Les couleurs dont on epaissit les traits trop fins (#397), par leur nom dans le rapport
   * (`blanc`, `blanc-2`). Une pastille se creuse sur 1 mm et un trait de couleur plus fin que
   * `pastille_largeur_min_mm` ne sort pas : une buse de 0,4 mm n'y pose pas deux cordons, et l'outil
   * le jetait en silence. Epaissi, il passe a cette largeur. **Facultative et nullable**, comme
   * `sol_hauteur_mm` : absente, `null` ou vide, rien n'est epaissi, et c'est le comportement
   * d'avant. La page n'envoie la cle que lorsque la liste n'est pas vide et que les couleurs sont
   * gardees. Les doublons passent (l'outil les dedoublonne) ; huit noms au plus, autant que de
   * couleurs. Un nom que l'image n'a pas est sans effet, avec un avertissement de l'outil.
   */
  couleurs_epaissies: z.array(z.string().regex(NOM_MOTIF)).max(COULEURS_MAX).nullable().optional(),
});
export type Reglages = z.infer<typeof reglagesSchema>;

export const renduRequeteSchema = reglagesSchema.extend({
  /** Les octets PNG, JPEG ou WebP en base64, sans prefixe `data:`. */
  image: z.base64().min(1).max(IMAGE_BASE64_MAX),
  /**
   * Le nom du fichier depose : il ne sert qu'au rapport et aux messages de l'outil. 200 unites
   * au plus et aucun caractere de controle : la page le nettoie avant l'envoi.
   */
  nom_fichier: z
    .string()
    .max(NOM_FICHIER_MAX)
    .refine(
      (nom) => ![...nom].some((car) => estUnControle(car.codePointAt(0) ?? 0)),
      "caractere de controle dans le nom du fichier",
    )
    .optional(),
});
export type RenduRequete = z.infer<typeof renduRequeteSchema>;

/**
 * La cle d'une teinte du catalogue Bambu, celle du module Bobines : `bambu.<gamme>.<code
 * couleur>`, en minuscules ASCII et tirets. Seules les deux gammes unies de Nicolas y figurent,
 * PLA Basic et PLA Matte (#337) : une gamme Silk n'a pas de cle ici.
 */
export const CLE_BAMBU_MOTIF = /^bambu\.pla-(basic|matte)\.[a-z0-9-]+$/;

/** Une teinte du catalogue Bambu : son nom en francais et en anglais, sa couleur et son filament. */
export const teinteBambuSchema = z.object({
  cle: z.string().regex(CLE_BAMBU_MOTIF),
  gamme: z.enum(["PLA Basic", "PLA Matte"]),
  nom_fr: z.string(),
  nom_en: z.string(),
  hexa: z.string().regex(/^#[0-9A-F]{6}$/),
  fila_id: z.string(),
});
export type TeinteBambu = z.infer<typeof teinteBambuSchema>;

/**
 * Le catalogue des teintes (#337) : `GET /api/silhouettes/bambu` rend ce que `products` rend a
 * `POST /silhouette/bambu`. Les habituelles de Nicolas viennent en premier dans la page.
 */
export const catalogueBambuSchema = z.object({
  teintes: z.array(teinteBambuSchema).min(1),
  habituelles: z.array(z.string().regex(CLE_BAMBU_MOTIF)),
});
export type CatalogueBambu = z.infer<typeof catalogueBambuSchema>;

/** Une couleur du rendu : la base d'abord, puis les pastilles, dans l'ordre des extrudeurs. */
export const couleurRapportSchema = z.object({
  nom: z.string(),
  hexa: z.string(),
  role: z.enum(["base", "pastille"]),
  volume_mm3: z.number().optional(),
  surface_mm2: z.number().optional(),
  morceaux: z.number().int().optional(),
  /**
   * La teinte Bambu que l'outil propose pour cette couleur (#337) : un choix que la page laisse
   * changer, pas une decision. **Facultative** : absente de toute reponse tant que `products`
   * n'est pas deploye. Jamais `null` : l'outil la donne, ou la cle manque.
   */
  bambu: teinteBambuSchema.optional(),
});
export type CouleurRapport = z.infer<typeof couleurRapportSchema>;

/**
 * Les traits de couleur d'une couleur du rendu (#397) : la part de sa pastille plus fine que la
 * largeur minimale, que l'outil jette ou, a la demande de la page (`couleurs_epaissies`), epaissit.
 * Une couleur dont tous les traits sont fins n'a plus de pastille : elle disparait de `couleurs`,
 * mais reste ici, et c'est par la que la page peut encore la proposer.
 */
export const traitDeCouleurSchema = z.object({
  /** Le nom de la couleur, celui du rapport. La base n'y figure jamais : ses traits relevent de `mode`. */
  nom: z.string(),
  hexa: z.string(),
  /** La surface des traits plus fins que la largeur minimale, mesuree **avant** tout traitement. */
  surface_mm2: z.number(),
  /** Cette surface rapportee a celle du trace entier de la couleur, avant traitement. */
  part_pct: z.number(),
  /**
   * `retires` : ils ne sortent pas ; `epaissis` : la couleur etait dans `couleurs_epaissies`. **Une
   * chaine libre**, comme le reste du rapport est `loose` : un `products` plus recent peut en ajouter
   * une troisieme, et la page ne doit pas tomber en 502 pour une valeur qu'elle ne lit pas. Elle ne
   * lit que `epaissis` (le bilan) : toute autre valeur n'est pas prise pour des traits epaissis.
   */
  traitement: z.string(),
});
export type TraitDeCouleur = z.infer<typeof traitDeCouleurSchema>;

/**
 * Le rapport de l'outil (`asdict(Rapport)`), sans aucun chemin du serveur. Seuls les champs
 * que la page lit sont decrits ; les autres passent tels quels (`loose`), pour qu'un champ
 * ajoute a l'outil n'arrete pas la console.
 */
export const rapportSchema = z
  .object({
    hauteur_hors_tout_mm: z.number(),
    largeur_mm: z.number(),
    epaisseur_mm: z.number(),
    masse_pla_g: z.number(),
    triangles: z.number().int(),
    etanche: z.boolean(),
    corps: z.number().int(),
    mode: z.enum(MODES),
    largeur_min_mm: z.number(),
    /** `canal alpha`, `seuillage Otsu` ou `seuil impose`. */
    origine_du_masque: z.string(),
    matiere_ajoutee_mm2: z.number(),
    part_trop_fine_pct: z.number(),
    ilots_rattaches: z.number().int(),
    trous_bouches: z.number().int(),
    zones_remplies: z.number().int(),
    zones_remplies_mm2: z.array(z.number()),
    /** Largeur d'appui sur la barre de l'attache, ou `null` sans attache. */
    contact_barre_mm: z.number().nullable(),
    /** Longueur de la ligne de sol ajoutee, ou `null` quand il n'y en a pas. */
    ligne_de_sol_mm: z.number().nullable(),
    /** Hauteur de cette ligne, ou `null` quand il n'y en a pas. */
    sol_hauteur_mm: z.number().nullable(),
    /**
     * Vrai quand l'outil a **releve la ligne de sol tout seul** pour attraper un second appui
     * (#175) : la piece ne posait que sur un pied, et la hauteur par defaut ne suffisait pas.
     * Faux quand la hauteur est celle par defaut ou celle que Nicolas a imposee. **Facultatif
     * et nullable**, comme les cotes du serre-livre : absent de toute reponse tant que
     * `products` n'est pas deploye, et la page ne doit pas tomber en 502 le temps du
     * deploiement.
     */
    sol_hauteur_relevee: z.boolean().nullable().optional(),
    /*
     * Le degagement du serre-livre (#166). Entre la face du mur du Bookend et le bord de la
     * fente, il ne reste que 6,75 mm, sur les 96 premiers millimetres au-dessus de la barre :
     * une silhouette n'entre que si le debord tourne vers le mur tient dessous. **C'est
     * `products` qui juge**, degagement en main, et qui dit quel sens passe : la console n'a
     * pas cette cote et ne saurait pas la deduire des debords. Les six champs sont
     * **facultatifs et nullables** : absents sans attache, absents d'un profil qui n'a pas ces
     * cotes, et absents de toute reponse tant que `products` n'est pas deploye. Une reponse
     * sans eux reste valide, sans quoi la page tomberait en 502 le temps d'un deploiement.
     */
    /** De combien la silhouette deborde des bouts de sa barre, sous la hauteur du mur. */
    debord_gauche_mm: z.number().nullable().optional(),
    debord_droit_mm: z.number().nullable().optional(),
    /** Vrai quand la piece entre dans le serre-livre. */
    serre_livre: z.boolean().nullable().optional(),
    /** Le cote a tourner vers le mur, `les deux` quand les deux sens entrent. */
    serre_livre_cote: z.enum(["gauche", "droite", "les deux"]).nullable().optional(),
    /**
     * Le degagement utile contre le mur, jeu de la barre deduit : 6,63 mm (6,75 moins 0,124),
     * le seuil nominal ne gardant aucune marge d'impression. L'outil le publie pour qu'on
     * juge la marge d'un debord ; la page ne l'affiche pas.
     */
    serre_livre_degagement_mm: z.number().nullable().optional(),
    /** La plus grande hauteur qui entrerait, quand ca n'entre pas. */
    serre_livre_hauteur_max_mm: z.number().nullable().optional(),
    /*
     * Ce que le mode serre-livres ajoute au rapport (#337). Comme les six champs du dessus, **tous
     * facultatifs et nullables** : la page doit survivre a un `products` pas encore deploye.
     */
    /** Le `cote_mur` recu, celui de la vue de face avant tout miroir ; `null` sans lui. */
    serre_livre_cote_mur: z.enum(COTES_MUR).nullable().optional(),
    /**
     * La plus grande hauteur, en millimetres entiers, ou le cote `serre_livre_cote_mur` entre en
     * gardant son sens. `null` quand il entre deja, sans `cote_mur`, ou si rien n'entre jusqu'au
     * plancher.
     */
    serre_livre_hauteur_cote_mm: z.number().int().nullable().optional(),
    /**
     * Le cote ou entre la hauteur conseillee `serre_livre_hauteur_max_mm`, `les deux` quand elle
     * entre des deux : jusqu'ici, seulement dans le texte de l'avertissement.
     */
    serre_livre_hauteur_max_cote: z.enum(["gauche", "droite", "les deux"]).nullable().optional(),
    /** La barre dans la vue de face : `[x0, x1]`, en mm depuis le bord gauche de la piece. */
    barre_mm: z.tuple([z.number(), z.number()]).nullable().optional(),
    /*
     * Les traits de couleur (#397). Comme les champs du dessus, **facultatifs et nullables** : la
     * page doit survivre a un `products` pas encore deploye, qui ne les envoie pas, et la ligne
     * « Traits de couleur » ne s'affiche alors pas. `null` quand les couleurs ne sont pas gardees
     * (pas demandees, ou pas separables : la piece sort d'un seul filament) ; presents, 0,8 et une
     * liste qui peut etre vide, des que les couleurs le sont, meme pour une piece d'une seule couleur.
     */
    /** Sous cette largeur, en mm, un trait de couleur ne sort pas : 0,8, deux cordons d'une buse de 0,4. */
    pastille_largeur_min_mm: z.number().nullable().optional(),
    /** Une entree par couleur (hors base) aux traits trop fins, dans l'ordre de la palette ; vide sinon. */
    traits_de_couleur: z.array(traitDeCouleurSchema).nullable().optional(),
    /** `null` : une seule couleur. */
    couleurs: z.array(couleurRapportSchema).nullable(),
    /** Les messages de l'outil, en ASCII, qui citent ses options de ligne de commande. */
    avertissements: z.array(z.string()),
    /** Des noms de fichiers seuls, jamais un chemin. */
    fichiers: z.array(z.string()),
  })
  .loose();
export type Rapport = z.infer<typeof rapportSchema>;

/**
 * Un fichier rendu, en base64. `fichierRenduSchema` et non `fichierSchema` : le portefeuille
 * a deja un `fichierSchema` (un fichier markdown d'un dossier d'idee), et les deux contrats
 * sortent par le meme pont `contract.ts`.
 */
export const fichierRenduSchema = z.object({
  nom: z.string(),
  type: z.string(),
  contenu: z.base64(),
});
export type FichierRendu = z.infer<typeof fichierRenduSchema>;

export const renduReponseSchema = z.object({
  rapport: rapportSchema,
  /** Le rapport tel que la ligne de commande l'affiche, sans les lignes `Fichier`. */
  texte: z.string(),
  /** L'apercu en PNG, ou `null` quand il n'a pas pu s'ecrire. */
  apercu_png: z.base64().nullable(),
  /**
   * La vue de face (#337) : un SVG avec un `path` par couleur, marque `data-couleur`, en
   * millimetres. C'est du balisage rendu par `products` : la page ne l'insere pas tel quel.
   * **Facultative et nullable** : absente tant que `products` n'est pas deploye, `null` sans
   * apercu. 600 000 caracteres au plus.
   */
  face_svg: z.string().max(FACE_SVG_MAX).nullable().optional(),
  /** `<nom>.3mf` puis `<nom>-stl.zip`. */
  fichiers: z.array(fichierRenduSchema),
  /** Vrai quand `products` a du reduire une image de plus de 2 500 px de cote. */
  image_reduite: z.boolean(),
});
export type RenduReponse = z.infer<typeof renduReponseSchema>;

/** Ce que la page lit a l'ouverture : les reglages par defaut et les limites. */
export const silhouettesInfoSchema = z.object({
  reglages: reglagesSchema,
  limites: z.object({
    image_source_octets_max: z.number(),
    image_envoi_octets_max: z.number(),
    cote_envoi_px: z.number(),
    cote_envoi_opaque_px: z.number(),
    calcul_delai_s: z.number(),
  }),
});
export type SilhouettesInfo = z.infer<typeof silhouettesInfoSchema>;

/** Les reglages de `scripts/silhouette.ps1` : 121 mm, soit 125 hors tout avec la barre. */
export const REGLAGES_PAR_DEFAUT: Reglages = {
  nom: "silhouette",
  hauteur_mm: 121,
  attache: ATTACHE_BASE_MODULAIRE,
  couleurs: "auto",
  sol: "auto",
  mode: "fidele",
};

/*
 * Le mode serre-livres (#337) : deux figurines posees sur un serre-livres, un jeu de fichiers par
 * imprimante. Chaque figurine est d'abord rendue seule (`renduReponseSchema`, dont la page garde
 * le 3MF), puis les deux sont assemblees : `POST /api/silhouettes/serre-livres`, qui fait suivre
 * la meme requete a `POST /silhouette/serre-livres` de `products`. Rien n'est garde sur fortress.
 */

/** Le nom d'une figurine, en caracteres : la limite de `_nom_affiche` (`service.py`). */
export const NOM_AFFICHE_MAX = 30;

/** Des lettres et des chiffres de tout alphabet, l'espace, `&`, `'` et `-`. */
const NOM_AFFICHE_MOTIF = /^[\p{L}\p{N} &'-]+$/u;

/**
 * La forme sous laquelle la page envoie un nom : NFC. `service.py` ne normalise rien, et un accent
 * separe de sa lettre (la forme NFD que macOS peut produire) n'est ni une lettre ni un chiffre
 * pour lui : il refuserait un « Salamèche » ainsi ecrit. **La page valide la saisie avec
 * `nomAfficheValide` et envoie `nomAfficheNormalise(saisie)`** ; le schema de la requete n'accepte
 * que cette forme.
 */
export function nomAfficheNormalise(nom: string): string {
  return nom.normalize("NFC");
}

/**
 * Le nom d'une figurine est-il acceptable ? La regle de `_nom_affiche` (`service.py`), appliquee a
 * la forme NFC du nom, celle que la page envoie : de 1 a 30 points de code (le `len` de Python, pas
 * les unites UTF-16), sans espace au bord, et que des lettres, des chiffres, des espaces, `&`, `'`
 * et `-`. `\p{L}\p{N}` et `str.isalnum()` designent les memes caracteres a version d'Unicode egale
 * (compares sur tout l'espace Unicode le 5 octobre 2026, Python 3.14 contre Node 22). Un caractere
 * ajoute a Unicode apres la version de Python du serveur passe ici et est refuse par l'outil, par
 * un 400 qui dit pourquoi.
 */
export function nomAfficheValide(nom: string): boolean {
  const forme = nomAfficheNormalise(nom);
  return (
    NOM_AFFICHE_MOTIF.test(forme) &&
    !forme.startsWith(" ") &&
    !forme.endsWith(" ") &&
    [...forme].length <= NOM_AFFICHE_MAX
  );
}

/** Le nom de fichier de l'outil, en caracteres : `NOM_FICHIER_MAX` de `assemblage.py`. */
const ARDOISE_MAX = 40;

const sansTiretsAuxBords = (texte: string): string => texte.replace(/^-+|-+$/g, "");

/**
 * Le nom qu'on tape, en nom de fichier : minuscules ASCII, chiffres et tirets. C'est `ardoise` de
 * `assemblage.py`, etape par etape : NFKD, ce qui n'est pas ASCII tombe, minuscules, chaque suite
 * d'autres caracteres devient un tiret, 40 caracteres au plus, « figurine » quand il ne reste rien.
 * Une lettre sans decomposition (ß, ø, ł, œ) tombe donc, comme chez l'outil. **Pour l'affichage
 * seulement** : c'est le serveur qui nomme les fichiers. Un assistant d'affichage, identique a
 * `ardoise` de l'outil, garde pour le jour ou la page montrera le nom de fichier d'avance ; la
 * page ne s'en sert pas aujourd'hui (elle montre le nom des plateaux, `silhouettes-jeux.ts`).
 */
export function nomVersArdoise(nom: string): string {
  const ascii = nom.normalize("NFKD").replace(/\P{ASCII}/gu, "");
  const tirets = ascii.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return sansTiretsAuxBords(sansTiretsAuxBords(tirets).slice(0, ARDOISE_MAX)) || "figurine";
}

/**
 * Les deux 3MF d'un assemblage, ensemble, en caracteres de base64 (un octet chacun) : ce que la
 * page s'autorise a envoyer, a verifier avant l'envoi. Le frontal nginx du template refuse un corps
 * de plus de 2 Mo (`client_max_body_size 2m`), par une page HTML et non du JSON : cette limite
 * laisse la place des noms et des teintes. **Le schema ne la fait pas respecter** : c'est la page
 * qui s'arrete avant d'envoyer, car un corps refuse par nginx n'arrive jamais jusqu'au backend.
 */
export const SERRE_LIVRES_ENVOI_OCTETS_MAX = 1_450_000;

/** Une figurine de l'assemblage : son 3MF rendu, son nom, son sens, la teinte de chaque couleur. */
export const figureSerreLivresSchema = z.object({
  /** Le 3MF de la reponse de rendu (`fichiers[0].contenu`), tel quel, en base64. */
  fichier_3mf: z.base64().min(1),
  /**
   * Le nom affiche, **sous sa forme NFC** (`nomAfficheNormalise`) : la seule que `service.py`
   * accepte pour un nom a accent. Un nom qui n'a pas ete normalise est refuse ici, des la console,
   * plutot que par l'outil. Tout nom accepte ici est donc accepte par l'outil, et la forme
   * normalisee d'un nom que la page dit valide l'est ici.
   */
  nom: z
    .string()
    .refine(
      (nom) => nomAfficheNormalise(nom) === nom && nomAfficheValide(nom),
      "nom de figurine invalide : lettres, chiffres, espaces, & ' et -, 30 caracteres au plus, forme NFC",
    ),
  /** Retournee en X a l'assemblage, la face coloree toujours dessous. */
  miroir: z.boolean(),
  /**
   * Une cle du catalogue pour **chaque** piece du 3MF, par son nom : le nom de couleur de l'outil,
   * ou `base` pour une figurine d'une seule couleur. L'outil refuse une piece sans teinte et une
   * teinte sans piece (400).
   */
  teintes: z.record(z.string().regex(NOM_MOTIF), z.string().regex(CLE_BAMBU_MOTIF)),
});
export type FigureSerreLivres = z.infer<typeof figureSerreLivresSchema>;

export const serreLivresRequeteSchema = z.object({
  gauche: figureSerreLivresSchema,
  droite: figureSerreLivresSchema,
  /** Le filament des Bookends et des Socles. */
  teinte_base: z.string().regex(CLE_BAMBU_MOTIF),
});
export type SerreLivresRequete = z.infer<typeof serreLivresRequeteSchema>;

/** Un jeu de fichiers : ce qui s'imprime sur une imprimante, plateau par plateau. */
export const jeuSchema = z.object({
  imprimante: z.enum(["p1s", "a1mini"]),
  /** Le nom de chaque plateau du 3MF, dans l'ordre ; vide quand l'imprimante refuse le jeu. */
  plateaux: z.array(z.string()),
  /** Pourquoi l'imprimante ne peut pas recevoir une figurine (le message de l'outil), sinon `null`. */
  refus: z.string().nullable(),
});
export type Jeu = z.infer<typeof jeuSchema>;

export const serreLivresReponseSchema = z.object({
  /**
   * Un 3MF par imprimante qui accepte le jeu, la P1S d'abord : le serveur les nomme
   * (`<nom>-<nom>-p1s.3mf`, `<nom>-<nom>-a1-mini.3mf`). La P1S sort toujours ; l'A1 mini peut
   * refuser sans que l'autre en souffre, et `fichiers` n'a alors que le 3MF de la P1S.
   */
  fichiers: z.array(fichierRenduSchema),
  /** Toujours deux jeux, dans l'ordre : la P1S, puis l'A1 mini. */
  jeux: z.array(jeuSchema).length(2),
  /** Les filaments du jeu : celui de la base d'abord, puis ceux des figurines, sans doublon. */
  filaments: z.array(teinteBambuSchema),
  /** La nomenclature, une ligne par filament : `<gamme> <nom anglais>`. */
  bom: z.array(z.string()),
});
export type SerreLivresReponse = z.infer<typeof serreLivresReponseSchema>;
